import { useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';
import { useAutomationUserContext } from './useAutomationUserContext';
import { getErrorMessage } from '@/utils/error';

export interface SxmTarget {
  id: number;
  orgId: number;
  name: string;
  role?: string;
  industry?: string;
  country?: string;
  language?: string;
  monthlyAppointmentsTarget?: number;
  monthlyLeadsTarget?: number;
  monthlyRevenueTarget?: number;
  createdBy?: string;
  createdAt: Date;
}

export interface CreateSxmTargetDto {
  name?: string;
  role?: string;
  industry?: string;
  country?: string;
  language?: string;
  monthlyAppointmentsTarget?: number;
  monthlyLeadsTarget?: number;
  monthlyRevenueTarget?: number;
}

export interface UpdateSxmTargetDto {
  name?: string;
  role?: string;
  industry?: string;
  country?: string;
  language?: string;
  monthlyAppointmentsTarget?: number;
  monthlyLeadsTarget?: number;
  monthlyRevenueTarget?: number;
}

export const useSxmTargets = (enabled: boolean = false) => {
  const { user, targetUserId, isInternalUser } = useAutomationUserContext();
  const [targets, setTargets] = useState<SxmTarget[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTargets = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const endpoint = isInternalUser && targetUserId !== user?.id ? '/sxm/internal/targets' : '/sxm/targets';
      const { data } = await api.get(endpoint, { params: { userId: targetUserId } });
      setTargets(data);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [targetUserId, enabled, isInternalUser, user?.id]);

  const createTarget = async (dto: CreateSxmTargetDto): Promise<SxmTarget> => {
    try {
      const { data: newTarget } = await api.post('/sxm/targets', dto, { params: { userId: targetUserId } });
      if (enabled) {
        await fetchTargets();
      } else {
        setTargets(prev => [...prev, newTarget]);
      }
      return newTarget;
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  };

  const updateTarget = async (id: number, dto: UpdateSxmTargetDto): Promise<SxmTarget> => {
    try {
      const { data: updatedTarget } = await api.patch(`/sxm/targets/${id}`, dto, { params: { userId: targetUserId } });
      setTargets(prev => prev.map(target => target.id === id ? updatedTarget : target));
      return updatedTarget;
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  };

  const deleteTarget = async (id: number): Promise<void> => {
    try {
      await api.delete(`/sxm/targets/${id}`, { params: { userId: targetUserId } });
      setTargets(prev => prev.filter(target => target.id !== id));
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  };

  useEffect(() => {
    if (enabled) {
      fetchTargets();
    }
  }, [enabled, fetchTargets]);

  return {
    targets,
    loading,
    error,
    createTarget,
    updateTarget,
    deleteTarget,
    refetch: fetchTargets,
  };
};
